import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal, Signal } from '@angular/core';
import { DataService } from '@app/shared/services/data-service';
import { OshDTO } from '@app/shared/services/dtos/osh/osh.dto';
import { LoaderService } from '@app/shared/services/shared/loader.service';
import { ToastService } from '@app/shared/services/shared/toast.service';
import { CreateOrEditOshAnalysisDialogData, Osh } from '@app/shared/types/models/osh/osh.model';
import { DataStore } from '@app/store/data-store';
import { IdsCollection } from '@app/store/ids-collection';
import { environment } from '@env/environment';
import { TranslateService } from '@ngx-translate/core';
import { OshAnalysisFactory } from './factories/osh-analysis.factory';

@Injectable({ providedIn: 'root' })
export class CompanyOshAnalysisDataService extends DataService<OshDTO, Osh> {
  private readonly httpClient: HttpClient = inject(HttpClient);
  private readonly loaderService: LoaderService = inject(LoaderService);
  private readonly oshAnalysisFactory: OshAnalysisFactory = inject(OshAnalysisFactory);
  private readonly toastService: ToastService = inject(ToastService);
  private readonly translateService: TranslateService = inject(TranslateService);

  private oshAnalyses: DataStore<Osh> = new DataStore<Osh>();
  private companyOshAnalyses: DataStore<IdsCollection> = new DataStore<IdsCollection>();

  public getCompanyOshAnalyses(companyId: string): Signal<Osh[] | undefined> {
    if (!companyId) {
      return signal(undefined);
    }

    if (!this.companyOshAnalyses.hasDataForId(companyId)) {
      this.fetchCompanyOshAnalyses(companyId);
    }

    return computed(
      () =>
        this.companyOshAnalyses
          .get(companyId)()
          ?.ids?.map(id => this.oshAnalyses.get(id)()) || []
    );
  }

  public getById(analysisId: string): Signal<Osh | undefined> {
    return computed(() => this.oshAnalyses.get(analysisId)());
  }

  public loadCompanyOshAnalyses(companyId: string, force = false): void {
    if (!force && this.companyOshAnalyses.hasDataForId(companyId)) {
      return;
    }
    this.fetchCompanyOshAnalyses(companyId);
  }

  public upsertLocalData(dto: OshDTO): Signal<Osh> {
    return dto ? this.oshAnalyses.upsert(this.oshAnalysisFactory.produce(dto)) : signal(undefined);
  }

  protected fetchCompanyOshAnalyses(companyId: string): void {
    // this.loaderService.startLoading();

    this.httpClient
      .get<OshDTO[]>(environment.APIEndPoint + `companies/${companyId}/osh`)
      .subscribe((projectOshAnalyses: OshDTO[]) => {
        this.oshAnalyses.upsertMany(projectOshAnalyses.map(itm => this.oshAnalysisFactory.produce(itm)));
        this.companyOshAnalyses.upsert({
          id: companyId,
          ids: projectOshAnalyses.map(itm => itm.id)
        });
      });
  }

  public deleteOshAnalysis(companyId: string, oshId: string): void {
    this.loaderService.startLoading();

    this.httpClient.delete<void>(`${environment.APIEndPoint}companies/${companyId}/osh/${oshId}`).subscribe({
      next: () => {
        this.toastService.success(this.translateService.instant('osh.toasts.successfully-deleted'));
        const existingProjectIndustries: string[] = this.companyOshAnalyses.get(companyId)().ids ?? [];
        this.companyOshAnalyses.upsert({
          id: companyId,
          ids: existingProjectIndustries.filter(id => id !== oshId)
        });
        this.oshAnalyses.delete(oshId);
      },
      error: () => this.loaderService.stopLoading(),
      complete: () => this.loaderService.stopLoading()
    });
  }

  public addCompanyOshAnalysis(companyId: string, osh: CreateOrEditOshAnalysisDialogData): void {
    this.loaderService.startLoading();

    this.httpClient.post<OshDTO>(`${environment.APIEndPoint}companies/${companyId}/osh/`, osh).subscribe({
      next: (oshDTO: OshDTO) => {
        this.upsertLocalData(oshDTO);
        this.toastService.success(this.translateService.instant('osh.toasts.successfully-added'));
        const existingProjectOshAnalyses: string[] = this.companyOshAnalyses.get(companyId)().ids ?? [];
        this.companyOshAnalyses.upsert({
          id: companyId,
          ids: existingProjectOshAnalyses.concat(oshDTO.id)
        });
      },
      error: () => this.loaderService.stopLoading(),
      complete: () => this.loaderService.stopLoading()
    });
  }

  public editCompanyOshAnalysis(companyId: string, osh: CreateOrEditOshAnalysisDialogData): void {
    this.loaderService.startLoading();

    this.httpClient.put<OshDTO>(`${environment.APIEndPoint}companies/${companyId}/osh/${osh.id}`, osh).subscribe({
      next: (oshDTO: OshDTO) => {
        this.upsertLocalData(oshDTO);
        this.toastService.success(this.translateService.instant('osh.toasts.successfully-updated'));
      },
      error: () => this.loaderService.stopLoading(),
      complete: () => this.loaderService.stopLoading()
    });
  }
}
